import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import _ from 'lodash';
import { DayPicker } from 'react-day-picker';
import Popup from "reactjs-popup";
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
dayjs.extend(minMax);
var quarterOfYear = require('dayjs/plugin/quarterOfYear')
dayjs.extend(quarterOfYear)
import advancedFormat from 'dayjs/plugin/advancedFormat' // load on demand
dayjs.extend(advancedFormat)

import { rangeToString } from 'utils/time';
import { useDidMount } from 'utils/components';

import AppState, { selectAppDateRange, selectReportDateRange } from 'store/AppState';

import Button, { IconButton } from 'components/Button';
import { CalendarIcon } from 'components/Svg';
import Scrollbars from 'components/Scrollbars';

const DateRanges = {
  LAST_MONTH: 'last_month',
  MONTH: 'month',
  DAYS30: '30days',
  DAYS7: '7days',
  HOURS24: '24hours', 
  CUSTOM: 'custom',
}

const _compareDates = (selected, from, to) => Boolean(selected?.from?.valueOf?.() === from?.valueOf() && selected?.to?.valueOf() === to?.valueOf())

const _generateTimePeriods = (period, count) => {
  let dateRanges = [];
  for (let i = 0; i < count; i++) {
    let currentMonth = dayjs().subtract(i, period);
    let from = currentMonth.startOf(period).toDate();
    let to = dayjs.min(currentMonth.endOf(period), dayjs().endOf('day')).toDate();
    dateRanges.push({ from, to });
  }
  return dateRanges;
}

const _generateMonths = _generateTimePeriods('months', ?);
const _generateQuarters = _generateTimePeriods('quarters', ?);
const _generateYears = _generateTimePeriods('years', ?);


export const DateRangeButton = React.forwardRef(({children, loading, label, size = 'm', ...props}, ref) => {
  return (
    <Button 
      ref={ref}
      size={size}
      color="light"
      disabled={loading}
      className={cx("g-date-range-btn", { 'popup-target': open })}
      {...props}>
      <CalendarIcon /> {label}
    </Button>);
});



export const DateRangePicker = ({ trigger, range, dates, onSave, }) => {
  const [selectedDays, setSelectedDays] = useState({ from: null, to: null}) ;
  const [showMonth, setShowMonth] = useState(dayjs().subtract(1, 'month').toDate())
  const [selectedRange, setSelectedRange] = useState(DateRanges.MONTH);
  const [showGroup, setShowGroup] = useState('recent');

  const visibleMonths = { 
    from: dayjs("2019-01-01").toDate(),
    to: dayjs().startOf('month').toDate(),
  };
  const futureDays = [
    { from: dayjs().add(1, 'day').toDate(), to: dayjs().add(1, 'day').endOf('month').toDate() }
  ];
  const modifiers = { start: selectedDays.from, end: selectedDays.to };
  const totalDays = dayjs(selectedDays.to).diff(selectedDays.from, 'days') + 1;
  const popRef = useRef();
  

  const shortcuts = {
    recent: {
      label: 'Recent',
      values: [
        { children: 'This Month', onClick: () => onRangeChange(DateRanges.MONTH), selected: selectedRange === DateRanges.MONTH },
        { children: '30 Days', onClick: () => onRangeChange(DateRanges.DAYS30), selected: selectedRange === DateRanges.DAYS30 },
        { children: '7 Days', onClick: () => onRangeChange(DateRanges.DAYS7), selected: selectedRange === DateRanges.DAYS7 },
        { children: '24 Hours', onClick: () => onRangeChange(DateRanges.HOURS24), selected: selectedRange === DateRanges.HOURS24 },
      ]
    },
    months: {
      label: 'Months',
      values: _.map(_generateMonths(6), ({from, to}) => ({ 
        children: dayjs(from).format('MMMM'),  
        onClick: () => setRange(DateRanges.CUSTOM, from, to),
        selected: _compareDates(selectedDays, from, to) }))
    },
    quarters: {
      label: 'Quarters',
      values: _.map(_generateQuarters(4), ({from, to}) => ({ 
        children: dayjs(from).format('[Q]Q YYYY'),  
        onClick: () => setRange(DateRanges.CUSTOM, from, to),
        selected: _compareDates(selectedDays, from, to) }))
    },
    years: {
      label: 'Years',
      values: _.map(_generateYears(2), ({from, to}) => ({ 
        children: dayjs(from).format('YYYY'),  
        onClick: () => setRange(DateRanges.CUSTOM, from, to),
        selected: _compareDates(selectedDays, from, to) }))
    },
  }
  
  useEffect(() => {
    setRange(range, dates.from, dates.to);
  }, []);

  const setDays = (from, to) => {
    to = dayjs.min(dayjs(to), dayjs()).toDate();
    setSelectedDays({ from, to });
    if(dayjs(from).month() === dayjs().month() && dayjs(from).year() === dayjs().year()) {
      setShowMonth(dayjs(from).subtract(1, 'month').toDate())
    } else {
      setShowMonth(from)
    }
  }

  const setRange = (range, from, to) => {
    setSelectedRange(range);
    switch(range) {
      case DateRanges.MONTH:
        return setDays(
          dayjs().startOf('month').startOf('day').toDate(),
          dayjs.min(dayjs().endOf('month'), dayjs()).toDate()
        );
      case DateRanges.DAYS30:
        return setDays(
          dayjs().subtract(30, 'days').startOf('day').toDate(),
          new Date(),
        );
      case DateRanges.DAYS7:
        return setDays(
          dayjs().subtract(7, 'days').startOf('day').toDate(),
          new Date(),
        );
      case DateRanges.HOURS24:
        return setDays(dayjs().subtract(24, 'hours').toDate(), new Date(),);
      case DateRanges.CUSTOM:
        return setDays(from,to);
    }
  }

  const onRangeChange = (range) => setRange(range);
  const onMonthChange = month => setShowMonth(month)
  const onDayClick = days => {
    if(!days) {
      days = { from: selectedDays.from, to: selectedDays.from};
    }
    let endTo = dayjs(days.to).endOf('day').toDate();
    setRange(DateRanges.CUSTOM, days.from, endTo);
  }

  const onGroupClick = group => setShowGroup(group)

  const onSaveClick = () => {
    onSave?.(selectedRange, selectedDays);
    popRef.current.closePopup();
  }
  
  return (
    <Popup 
      ref={popRef}
      trigger={trigger}
      keepTooltipInside={true}
      position="bottom right">
      <div className="g-date-range">
        <div className="range-list">
          <Scrollbars>
            { _.map(shortcuts, (group, key) => (
              <div key={key} className={cx("range-group", { selected: showGroup === key })} onClick={() => onGroupClick(key)}>
                <strong className="group-label">{group.label}</strong>
                <div className="group-list">
                  { _.map(group.values, ({selected, ...props}, idx) => <a key={idx} className={cx({selected})} { ...props } />) }
                </div>
              </div>
            )) }
          </Scrollbars>
        </div>
        <div className="cal-list">
          <DayPicker 
            mode="range"
            showOutsideDays={true}
            numberOfMonths={2}
            pagedNavigation={true}
            canChangeMonth={true}
            fixedWeeks={true}
            disabled={futureDays}
            month={showMonth}
            onMonthChange={onMonthChange}
            fromMonth={visibleMonths.from}
            toMonth={visibleMonths.to}
            fromDay
            modifiers={modifiers}
            selected={selectedDays}
            onSelect={onDayClick} />
            <div className="actions">
              <div className="days">{ totalDays } days selected</div>
              <Button size="s" onClick={onSaveClick}>Save</Button>
            </div>
        </div>
      </div>
    </Popup> );
  
}


export const AppDateRange = ({ disabled, onDateChange, ...props }) => {
  let { range, dates } = useSelector(selectAppDateRange);
  let dispatch = useDispatch();
  let label = rangeToString(range, dates);
  let onSaveClick = (r, d) => {
    dispatch(AppState.actions.setAppDateRange(r, d));
    onDateChange?.(r, d);
  }
  return (
    <DateRangePicker
      trigger={open => <div><DateRangeButton open={open} label={label} disabled={disabled} /></div>}
      range={range}
      dates={dates}
      disabled={disabled}
      onSave={onSaveClick} />
  );
}
export default AppDateRange;


export const ReportDateRange = ({ disabled, onDateChange, buttonProps, ...props }) => {
  let { range, dates } = useSelector(selectReportDateRange);
  let dispatch = useDispatch();
  let label = rangeToString(range, dates);
  let onSaveClick = (r, d) => {
    dispatch(AppState.actions.setReportDateRange(r, d));
    onDateChange?.(r, d);
  }
  return (
    <DateRangePicker
      trigger={open => <div><DateRangeButton open={open} label={label} disabled={disabled} {...buttonProps} /></div>}
      range={range}
      dates={dates}
      disabled={disabled}
      onSave={onSaveClick} />
  );
}
